import { environment } from '../../environments/environment';
import {
  brandAliases,
  brandHostMappings,
  brandSchemes,
} from './brand-routing';

export type BrandCode = 'gm' | 'gr' | 'gb' | 'bu' | 'ag' | 'mo' | 'dc';

function getCurrentHostname(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.location.hostname.toLowerCase();
}

function toHostname(value: string | undefined | null): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  try {
    return new URL(trimmed).hostname.toLowerCase();
  } catch {
    return trimmed.toLowerCase();
  }
}

export function normalizeBrandCode(
  value: string | undefined | null
): BrandCode | undefined {
  if (!value) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return brandAliases[normalized];
}

export function resolveHostBrandCode(
  hostname: string | undefined | null
): BrandCode | undefined {
  const normalizedHostname = toHostname(hostname);
  if (!normalizedHostname) {
    return undefined;
  }

  for (const [pattern, brand] of brandHostMappings) {
    if (pattern.test(normalizedHostname)) {
      return brand;
    }
  }

  return undefined;
}

export function resolveBrandCodeFromHostname(
  hostname: string | undefined | null
): BrandCode | undefined {
  return resolveHostBrandCode(hostname);
}

export function resolveBuildBrandCode(options: {
  hostname?: string;
  brandCode?: string;
  frontUrl?: string;
  buildLang?: string;
} = {}): BrandCode {
  const explicitBrand = normalizeBrandCode(options.brandCode ?? environment.BRAND_CODE);
  if (explicitBrand) {
    return explicitBrand;
  }

  const frontUrl = options.frontUrl ?? environment.FRONT_URL;
  const buildLang = options.buildLang ?? environment.LANG;

  return (
    resolveHostBrandCode(frontUrl) ??
    normalizeBrandCode(buildLang) ??
    'gm'
  );
}

export function resolveCurrentBrandCode(options?: {
  hostname?: string;
  brandCode?: string;
  frontUrl?: string;
  buildLang?: string;
}): BrandCode {
  return resolveBuildBrandCode(options);
}

export function isHostCompatibleWithBuild(options: {
  hostname?: string;
  brandCode?: string;
  frontUrl?: string;
  buildLang?: string;
} = {}): boolean {
  const hostname = options.hostname ?? getCurrentHostname();
  const hostBrand = resolveHostBrandCode(hostname);
  if (!hostBrand) {
    return true;
  }

  return hostBrand === resolveBuildBrandCode(options);
}

export function resolveBrandScheme(brandCode: BrandCode = resolveBuildBrandCode()): string {
  return `${brandSchemes[brandCode]}://`;
}

export function isLocaleAllowedOnCurrentFront(
  locale: string | undefined | null,
  options?: {
    hostname?: string;
    brandCode?: string;
    frontUrl?: string;
    buildLang?: string;
  }
): boolean {
  const userBrand = normalizeBrandCode(locale);
  if (!userBrand) {
    return false;
  }

  return userBrand === resolveBuildBrandCode(options);
}
